<template>
  <div v-frag>
    <CardIeaAppMain>
      <v-container fluid class="pa-4">
        <!-- HEADER SECTION -->
        <v-row align="center" class="mb-8">
          <v-col cols="12">
            <h1 class="text-h4 font-weight-bold bce-text-gradient">Mis Materias</h1>
            <p class="grey--text text--darken-1">Gestiona tus clases, recursos y progreso académico en un solo lugar.</p>
          </v-col>
        </v-row>

        <v-row>
          <!-- LEFT COLUMN: SUBJECT CARDS -->
          <v-col cols="12" lg="8">
            <div class="d-flex align-center justify-space-between mb-4">
              <h2 class="text-h5 font-weight-bold grey--text text--darken-3">Tus Inscripciones</h2>
              <v-btn-toggle v-model="favFilter" mandatory dense rounded class="bce-toggle">
                <v-btn small value="all">Todas</v-btn>
                <v-btn small value="fav">Favoritas</v-btn>
              </v-btn-toggle>
            </div>

            <v-row v-if="loadingMaterias" class="mt-4">
              <v-col v-for="n in 4" :key="n" cols="12" md="6">
                <v-skeleton-loader type="card" class="rounded-xl"></v-skeleton-loader>
              </v-col>
            </v-row>

            <v-row v-else class="mt-2">
              <v-col
                v-for="(mt, index) in filteredMaterias"
                :key="index"
                cols="12"
                md="6"
              >
                <v-card
                  class="bce-materia-card rounded-xl elevation-2 mb-4"
                  @click="irDetalleMateria(mt, index)"
                >
                  <v-card-text class="pa-5">
                    <v-row align="start" no-gutters>
                      <v-col cols="auto" class="mr-4">
                        <v-avatar size="64" :color="mt.color || '#E50050'" class="rounded-lg elevation-3">
                          <v-icon color="white" size="32">mdi-book-open-variant</v-icon>
                        </v-avatar>
                      </v-col>
                      <v-col>
                        <div class="d-flex justify-space-between align-start">
                          <h3 class="text-h6 font-weight-bold mb-1 bce-materia-title">
                            {{ mt.materiaName || mt.materia }}
                          </h3>
                          <v-btn
                            icon
                            small
                            @click.stop="toggleFav(mt)"
                            :color="mt.visible == 1 ? '#FFD700' : 'grey lighten-1'"
                          >
                            <v-icon>{{ mt.visible == 1 ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
                          </v-btn>
                        </div>
                        
                        <div class="d-flex align-center mt-1">
                          <v-chip
                            v-if="mt.code"
                            color="#E50050"
                            dark
                            x-small
                            class="px-2 font-weight-bold bce-live-badge mr-2"
                            @click.stop="abrirMeet(mt.code)"
                          >
                            <v-icon left size="14">mdi-circle-medium</v-icon>
                            EN VIVO
                          </v-chip>
                          <span class="text-caption grey--text">{{ mt.tipo || 'Cuatrimestral' }}</span>
                        </div>
                      </v-col>
                    </v-row>

                    <div class="mt-6">
                      <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-caption font-weight-bold grey--text text--darken-2">Progreso de Contenido</span>
                        <span class="text-caption font-weight-black" :style="{ color: mt.color || '#E50050' }">{{ mt.porcentaje }}%</span>
                      </div>
                      <v-progress-linear
                        :value="mt.porcentaje"
                        :color="mt.color || '#E50050'"
                        height="8"
                        rounded
                        class="bce-progress"
                      ></v-progress-linear>
                    </div>
                  </v-card-text>
                  
                  <v-divider></v-divider>
                  
                  <v-card-actions class="px-5 py-3">
                    <span class="footer-text grey--text">Explorar módulos</span>
                    <v-spacer></v-spacer>
                    <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
            
            <!-- FREE COURSES SECTION -->
            <div v-if="cursosGratis.length > 0" class="mt-12">
              <h2 class="text-h5 font-weight-bold grey--text text--darken-3 mb-6">Cursos Complementarios</h2>
              <v-row>
                <v-col v-for="cg in cursosGratis" :key="cg.IDStB" cols="6" sm="4" md="3">
                  <v-card
                    class="bce-free-course rounded-xl text-center pa-4"
                    elevation="1"
                    @click="abrirContenidoGratis(cg)"
                  >
                    <v-avatar color="red lighten-5" size="56" class="mb-3">
                      <v-icon color="#E50050">mdi-certificate-outline</v-icon>
                    </v-avatar>
                    <div class="text-caption font-weight-bold bce-truncate-2">{{ cg.nombre }}</div>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </v-col>

          <!-- RIGHT COLUMN: CALENDAR & INFO -->
          <v-col cols="12" lg="4">
            <v-card class="rounded-xl bce-calendar-card elevation-4 mb-6">
              <v-card-title class="d-flex align-center py-4 px-6 white--text bce-header-red">
                <v-icon color="white" class="mr-2">mdi-calendar-clock</v-icon>
                <span class="text-subtitle-1 font-weight-bold">Calendario de Exámenes</span>
              </v-card-title>
              
              <v-card-text class="pa-4">
                <div class="d-flex align-center justify-space-between mb-4 mt-2">
                  <v-btn icon small @click="changeMonth(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
                  <span class="text-h6 font-weight-bold text-capitalize">{{ monthName }} {{ currentYear }}</span>
                  <v-btn icon small @click="changeMonth(1)"><v-icon>mdi-chevron-right</v-icon></v-btn>
                </div>

                <v-simple-table dense class="bce-calendar-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th v-for="(d, index) in diasSemana" :key="index" class="text-center font-weight-bold grey--text">{{ d }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(semana, sIdx) in calendarWeeks" :key="sIdx">
                        <td
                          v-for="(dia, dIdx) in semana"
                          :key="dIdx"
                          class="text-center pa-1"
                        >
                          <v-badge
                            v-if="dia && hasEvent(dia)"
                            dot
                            overlap
                            color="#E50050"
                            class="bce-event-badge"
                          >
                            <v-btn
                              v-if="dia"
                              icon
                              x-small
                              :class="{ 'bce-today': isToday(dia) }"
                              class="font-weight-bold"
                              @click="showEvents(dia)"
                            >
                              {{ dia }}
                            </v-btn>
                          </v-badge>
                          <v-btn
                            v-else-if="dia"
                            icon
                            x-small
                            :class="{ 'bce-today': isToday(dia) }"
                            class="font-weight-bold grey--text text--darken-2"
                          >
                            {{ dia }}
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <!-- NEXT EVENTS LIST -->
                 <div class="mt-8 px-2">
                   <div class="text-subtitle-2 font-weight-bold mb-4 grey--text text--darken-3">Próximos Vencimientos</div>
                   <div v-if="upcomingEvents.length === 0" class="text-caption grey--text italic">No hay eventos próximos</div>
                   <div
                    v-for="(ev, idx) in upcomingEvents"
                    :key="idx"
                    class="d-flex align-center mb-4 pa-3 rounded-lg bce-event-item"
                   >
                     <div class="bce-event-indicator mr-3" :style="{ backgroundColor: ev.color || '#E50050' }"></div>
                     <div class="flex-grow-1">
                       <div class="text-caption font-weight-bold mb-0 lh-tight">{{ ev.titulo }}</div>
                       <div class="text-errors grey--text" style="font-size: 10px;">{{ ev.inicio | formatDate }}</div>
                     </div>
                   </div>
                 </div>
              </v-card-text>
            </v-card>

            <v-alert
              border="left"
              colored-border
              color="#E50050"
              elevation="2"
              class="rounded-xl bce-info-alert"
            >
              <div class="text-subtitle-2 font-weight-bold mb-1">¿Sabías que?</div>
              <div class="text-caption grey--text text--darken-2">
                Mantener tus favoritas al día te ayuda a priorizar tus estudios. Haz clic en la estrella para organizar tu semestre.
              </div>
            </v-alert>
          </v-col>
        </v-row>
      </v-container>
    </CardIeaAppMain>
  </div>
</template>

<script>
import CardIeaAppMain from "../../components/CardIeaAppMain";
import moment from "moment";
moment.locale("es");

export default {
  name: "MisMaterias",
  components: { CardIeaAppMain },
  filters: {
    formatDate(date) {
      if (!date) return "";
      return moment(date).format("DD [de] MMMM");
    }
  },
  data: () => ({
    materias: [],
    cursosGratis: [],
    eventosCalendario: [],
    loadingMaterias: true,
    favFilter: "all",
    currentMonth: new Date().getMonth(),
    currentYear: new Date().getFullYear(),
    diasSemana: ["D", "L", "M", "M", "J", "V", "S"],
  }),
  computed: {
    filteredMaterias() {
      let filtered = Array.isArray(this.materias) ? [...this.materias] : (this.materias ? Object.values(this.materias) : []);
      
      if (filtered.length === 0) return [];
      
      // 1. Sort by Favorites first (robust check for 1, '1', true, or truthy)
      filtered.sort((a, b) => {
        const valA = (a.visible == 1 || a.visible === true) ? 1 : 0;
        const valB = (b.visible == 1 || b.visible === true) ? 1 : 0;
        return valB - valA;
      });

      // 2. Filter if the user selected the "Only Favorites" tab
      if (this.favFilter === "fav") {
        return filtered.filter(m => (m.visible == 1 || m.visible === true));
      }
      
      return filtered;
    },
    monthName() {
      const meses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
      return meses[this.currentMonth];
    },
    calendarWeeks() {
      const firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();
      const lastDate = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
      const weeks = [];
      let currentWeek = new Array(7).fill(null);
      for (let d = 1; d <= lastDate; d++) {
        const pos = (firstDay + d - 1) % 7;
        if (pos === 0 && d > 1) {
          weeks.push(currentWeek);
          currentWeek = new Array(7).fill(null);
        }
        currentWeek[pos] = d;
      }
      weeks.push(currentWeek);
      return weeks;
    },
    upcomingEvents() {
      const hoy = new Date();
      hoy.setHours(0,0,0,0);
      return this.eventosCalendario
        .filter(e => new Date(e.fin + "T23:59:59") >= hoy)
        .sort((a,b) => new Date(a.inicio) - new Date(b.inicio))
        .slice(0, 5);
    }
  },
  mounted() {
    this.refreshData();
  },
  methods: {
    async refreshData() {
      this.loadingMaterias = true;
      await Promise.all([
        this.getMaterias(),
        this.getFreeCourses(),
        this.getEvents()
      ]);
      this.loadingMaterias = false;
    },
    async getMaterias() {
      try {
        const res = await this.$API.get({ url: "materias-new", data: {} });
        // Normalize response to ensure it's always an array
        this.materias = Array.isArray(res) ? res : (res ? Object.values(res) : []);
      } catch (e) {
        console.error(e);
        this.materias = [];
      }
    },
    async getFreeCourses() {
      try {
        const res = await this.$API.get({ url: "materias-gratis", data: {} });
        this.cursosGratis = res || [];
      } catch (e) { console.error(e); }
    },
    async getEvents() {
      try {
        const res = await this.$API.get({ url: "examen/proximos-materias-asignadas", data: {} });
        const raw = res?.eventos || [];
        this.eventosCalendario = raw.map(ev => ({
          titulo: ev.titulo || ev.materia || "Examen",
          inicio: (ev.inicio || ev.fecha_inicio || "").split(" ")[0],
          fin: (ev.fin || ev.fecha_fin || ev.fecha_final || "").split(" ")[0],
          color: ev.color || "#E50050"
        })).filter(e => e.inicio && e.fin);
      } catch (e) { console.error(e); }
    },
    toggleFav(materia) {
      // Use any available ID field to identify the enrollment
      const enrollmentId = materia.inscripcion_materia || materia.id_inscripcion || materia.id || materia.IDStB;
      if (!enrollmentId) {
        console.error("No se pudo encontrar el ID de inscripción para esta materia");
        return;
      }

      const newVisible = materia.visible == 1 ? 0 : 1;
      this.$API.post({
        url: "materia/setvisible",
        data: { id: enrollmentId, visible: newVisible }
      }).then(() => {
        materia.visible = newVisible;
        // The computed property filteredMaterias will automatically re-sort
      }).catch(err => {
        console.error("Error al actualizar favorita:", err);
      });
    },
    irDetalleMateria(mt, index) {
      const codCatedra = mt.cod_catedra || mt.cod || mt.id || mt.IDStB || index + 1;
      this.$router.push({
        name: "MisMateriaDetalle",
        params: { materiaId: String(codCatedra) },
        query: {
          nombre: mt.materiaName || mt.materia,
          progreso: String(mt.porcentaje || 0),
          color: mt.color || "#E50050",
          codCatedra: String(codCatedra)
        }
      });
    },
    abrirMeet(code) {
      window.open("https://meet.google.com/" + code, "_blank");
    },
    abrirContenidoGratis(cg) {
      window.open(`/materia/clases/${cg.uuid}/${cg.materia_id}/${cg.IDStB}`, "_blank");
    },
    changeMonth(dir) {
      let m = this.currentMonth + dir;
      if (m < 0) { this.currentMonth = 11; this.currentYear--; }
      else if (m > 11) { this.currentMonth = 0; this.currentYear++; }
      else { this.currentMonth = m; }
    },
    isToday(dia) {
      const h = new Date();
      return dia === h.getDate() && this.currentMonth === h.getMonth() && this.currentYear === h.getFullYear();
    },
    hasEvent(dia) {
      const check = moment(`${this.currentYear}-${this.currentMonth + 1}-${dia}`, "YYYY-MM-DD");
      return this.eventosCalendario.some(e => {
        const start = moment(e.inicio);
        const end = moment(e.fin);
        return check.isBetween(start, end, 'day', '[]');
      });
    }
  }
};
</script>

<style scoped>
.bce-text-gradient {
  background: linear-gradient(45deg, #1a237e 30%, #E50050 90%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.bce-toggle {
  background: #f5f5f5 !important;
  padding: 4px;
}

.bce-materia-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0,0,0,0.05) !important;
  cursor: pointer;
  overflow: hidden;
}

.bce-materia-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
}

.bce-materia-title {
  color: #2c3e50;
  line-height: 1.2;
}

.bce-live-badge {
  animation: pulse-red 2s infinite;
}

@keyframes pulse-red {
  0% { box-shadow: 0 0 0 0 rgba(229, 0, 80, 0.4); }
  70% { box-shadow: 0 0 0 8px rgba(229, 0, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(229, 0, 80, 0); }
}

.bce-progress {
  border-radius: 10px;
}

.bce-free-course {
  transition: all 0.2s;
  cursor: pointer;
  border: 1px solid #f0f0f0 !important;
}

.bce-free-course:hover {
  background: #fff5f5;
  border-color: #E50050 !important;
}

.bce-header-red {
  background: linear-gradient(135deg, #E50050 0%, #b3003e 100%);
}

.bce-calendar-table th {
  font-size: 11px;
  height: 30px;
}

.bce-today {
  background: #E50050 !important;
  color: white !important;
}

.bce-event-item {
  background: #fdfdfd;
  border: 1px solid #f0f0f0;
  transition: background 0.2s;
}

.bce-event-item:hover {
  background: #fff8f8;
}

.bce-event-indicator {
  width: 4px;
  height: 24px;
  border-radius: 2px;
}

.lh-tight { line-height: 1.2; }
.bce-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.bce-info-alert {
  background-color: white !important;
}
</style>